import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functionsEu as functions } from '../../firebase';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import './PublicBookingPage.css';
import '../Settings/GlobalNotices.css';
import { useIntl } from 'react-intl';
import { calculateAvailableTimes as calcAvailableTimes, getAvailableDates as getAvailDates } from '../../utils/bookingSlots';
import { fetchCombinedAvailabilities } from '../../utils/weeklySchedule';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { useTerugDiepte } from '../../hooks/useTerugKnop';
import { ensureExtraBrandingFontsLoaded, EXTRA_BRANDING_FONT_NAMES } from '../../utils/loadGoogleFont';
import { sanitizeFooterHtml } from '../../utils/sanitizeHtml';

function PublicBookingPage() {
    const { customLink } = useParams();
    const [userId, setUserId] = useState(null);
    const [services, setServices] = useState([]);
    const [availabilities, setAvailabilities] = useState([]);
    const [bookedSlots, setBookedSlots] = useState([]);
    const [selectedService, setSelectedService] = useState('');
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTime, setSelectedTime] = useState('');
    const [availableTimes, setAvailableTimes] = useState([]);
    const [customerName, setCustomerName] = useState('');
    const [currentStep, setCurrentStep] = useState('selectService');
    const [customerEmail, setCustomerEmail] = useState('');
    const [customerNote, setCustomerNote] = useState('');
    const [intakeAnswers, setIntakeAnswers] = useState({}); // { [questionId]: answer }
    const [teamMembers, setTeamMembers] = useState([]);
    const [selectedTeamMember, setSelectedTeamMember] = useState(null); // 'self' | teamMemberId | null
    const [isLoading, setIsLoading] = useState(true);
    const [customTitle, setCustomTitle] = useState('');
    const [pageTitle, setPageTitle] = useState('');
    const [bookingStep, setBookingStep] = useState(1);
    const [bookingConfirmed, setBookingConfirmed] = useState(false);
    const [monthlyBookingCount, setMonthlyBookingCount] = useState(0);
    const [showPoweredBy, setShowPoweredBy] = useState(true);
    const [logoUrl, setLogoUrl] = useState('');
    const [minimumBookingNoticeHours, setMinimumBookingNoticeHours] = useState(0);
    const [customFooter, setCustomFooter] = useState('');
    const [calendarStyle, setCalendarStyle] = useState('light');
    const [zoomMeetingUrl, setZoomMeetingUrl] = useState('');
    const [brandingSettings, setBrandingSettings] = useState({
        primaryColor: '#000000',
        secondaryColor: '#f8f9fa',
        buttonText: 'Boek Nu',
        buttonTextColor: '#ffffff',
        font: 'Arial'
    });
    const [isPremiumOrTester, setIsPremiumOrTester] = useState(false);
    const [isWaitlistEnabled, setIsWaitlistEnabled] = useState(false);
    const [waitlistName, setWaitlistName] = useState('');
    const [waitlistEmail, setWaitlistEmail] = useState('');
    const [waitlistJoined, setWaitlistJoined] = useState(false);
    const [isJoiningWaitlist, setIsJoiningWaitlist] = useState(false);
    const [isBrandingLoaded, setIsBrandingLoaded] = useState(false);
    const [paymentStatus, setPaymentStatus] = useState(null);
    const [invalidLink, setInvalidLink] = useState(false);
    const [notification, setNotification] = useState(null);
    const pageRef = useRef(null);
    // De eigen agenda van de aanbieder zoals de eerste lading die gaf. Een teamlid vervangt de
    // agenda in de state, dus bij terug en een andere keuze zetten we deze weer terug.
    const ownCalendarRef = useRef(null);
    const intl = useIntl();

    // Eigen in-app melding i.p.v. de kale browser-alert() die hier voorheen
    // overal gebruikt werd — zelfde stijl/kleuren als de rest van de app
    // (GlobalNotices.css, ook gebruikt door bv. BookingForm.js).
    useEffect(() => {
        if (notification) {
            const timer = setTimeout(() => setNotification(null), 4000);
            return () => clearTimeout(timer);
        }
    }, [notification]);

    // Eigen titel per aanbieder i.p.v. overal dezelfde statische "FlexiTimeBooking"
    // uit index.html — anders zijn bladwijzers voor meerdere boekingspagina's (of
    // voor deze pagina t.o.v. de app/het admin-paneel) niet van elkaar te onderscheiden.
    // Volgorde: eigen paginatitel → titel boven de kalender → standaardtekst.
    // "– FlexiTimeBooking" erachter volgt "Powered by": enkel weg bij een
    // betalend abonnement dat die uitgeschakeld heeft (Free toont 'm altijd).
    // Tijdens het laden een neutraal "Boeken…" (ook al gezet in index.html).
    const tabTitleBase = pageTitle || customTitle || intl.formatMessage({ id: 'publicBooking.tabTitleDefault' });
    useDocumentTitle(
        isLoading
            ? intl.formatMessage({ id: 'publicBooking.tabTitleLoading' })
            : (showPoweredBy ? `${tabTitleBase} – FlexiTimeBooking` : tabTitleBase)
    );

    // Meldt de effectieve hoogte van de pagina aan een eventuele bovenliggende
    // pagina via postMessage — nodig zodat een <iframe>-embed op de website
    // van de aanbieder (zie de embed-code in LinkSettings.js) zichzelf live
    // kan herschalen i.p.v. een vaste hoogte te gebruiken. Cross-origin iframes
    // kunnen hun eigen hoogte niet rechtstreeks aan de bovenliggende pagina
    // doorgeven — postMessage is hiervoor de standaard, veilige oplossing.
    useEffect(() => {
        if (!pageRef.current || window.self === window.top) {
            return; // Niet in een iframe — niets te doen.
        }
        const observer = new ResizeObserver(entries => {
            const height = Math.ceil(entries[0].contentRect.height);
            window.parent.postMessage({ type: 'flexitimebooking-resize', height }, '*');
        });
        observer.observe(pageRef.current);
        return () => observer.disconnect();
    }, [isLoading, isBrandingLoaded]);

    // Terugkeer van Stripe Checkout (betaalde dienst). De echte boeking wordt
    // asynchroon door de webhook aangemaakt, dus hier enkel het betaalresultaat
    // tonen — niet proberen de boeking zelf al op te halen/tonen.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('paymentSuccess') === 'true') {
            setPaymentStatus('success');
        } else if (params.get('paymentCancelled') === 'true') {
            setPaymentStatus('cancelled');
        }
    }, []);

    useEffect(() => {
        if (selectedDate && selectedService) {
            const times = calculateAvailableTimes(selectedDate, selectedService);
            setAvailableTimes(times);
        }
    }, [selectedDate, selectedService, bookedSlots]);

    useEffect(() => {
        setWaitlistJoined(false);
        setWaitlistName('');
        setWaitlistEmail('');
    }, [selectedDate, selectedService]);


    // Vult alle providerSettings-afgeleide state in één keer, uit de
    // gebundelde payload van getPublicBookingPageData hieronder — geen eigen
    // Firestore-read meer nodig, dat gebeurt al server-side.
    const applyProviderSettings = (providerSettings) => {
        if (!providerSettings) return;
        setLogoUrl(providerSettings.logoUrl || '');
        setMinimumBookingNoticeHours(providerSettings.minimumBookingNoticeHours || 0);
        setCustomTitle(providerSettings.customTitle || '');
        setPageTitle(providerSettings.pageTitle || '');
        setIsPremiumOrTester(providerSettings.isPremiumOrTester);
        setIsWaitlistEnabled(providerSettings.isWaitlistEnabled);
        if (providerSettings.brandingSettings) {
            setBrandingSettings(providerSettings.brandingSettings);
            if (EXTRA_BRANDING_FONT_NAMES.includes(providerSettings.brandingSettings.font)) {
                ensureExtraBrandingFontsLoaded();
            }
        }
        setCustomFooter(providerSettings.customFooter || '');
        setCalendarStyle(providerSettings.calendarStyle || 'light');
        setMonthlyBookingCount(providerSettings.monthlyBookingCount || 0);
        setShowPoweredBy(providerSettings.showPoweredBy);
    };

    // Eén Cloud Function-call (getPublicBookingPageData) i.p.v. een link-opzoeking
    // + 5 losse Firestore-reads die de klant vroeger zelf, over zijn eigen
    // verbinding, na elkaar/parallel moest doen. De functie doet dezelfde reads
    // server-side (over Google's eigen netwerk) en geeft alles in 1 round-trip
    // terug — zie functions/src/functions/booking/publicBookingPageData.js.
    useEffect(() => {
        const fetchInitialData = async () => {
            setIsLoading(true);
            try {
                const getPublicBookingPageData = httpsCallable(functions, 'getPublicBookingPageData');
                const { data } = await getPublicBookingPageData({ customLink });

                setUserId(data.userId);
                applyProviderSettings(data.providerSettings);
                setServices(data.services || []);
                setTeamMembers(data.teamMembers || []);
                const ownAvailabilities = (data.availabilities || []).map(a => ({
                    ...a,
                    date: new Date(a.date),
                    startTime: new Date(a.startTime),
                    endTime: new Date(a.endTime)
                }));
                const ownBookedSlots = (data.bookedSlots || []).map(slot => ({
                    dateTime: { seconds: Math.floor(new Date(slot.dateTime).getTime() / 1000) },
                    duration: slot.duration
                }));
                ownCalendarRef.current = { availabilities: ownAvailabilities, bookedSlots: ownBookedSlots };
                setAvailabilities(ownAvailabilities);
                setBookedSlots(ownBookedSlots);
                setIsBrandingLoaded(true);
            } catch (error) {
                console.error("Error fetching initial data:", error);
                setInvalidLink(true);
            } finally {
                setIsLoading(false);
            }
        };
        fetchInitialData();
    }, [customLink]);

    const getCalendarCustomStyles = () => {
        const isLight = calendarStyle === 'light';
        const { primaryColor, secondaryColor, buttonTextColor } = brandingSettings;

        const bgColor = isLight ? secondaryColor : primaryColor;
        const textColor = isLight ? primaryColor : secondaryColor;
        // Beschikbare/gekozen dagen zien eruit als de knoppen van de pagina
        // (getButtonStyle: Primaire Kleur + Knop Tekst Kleur). Voorheen stond
        // hier brandingSettings.buttonText — de knop*tekst* ("Boek Nu"), geen
        // kleur — waardoor de achtergrond wegviel en beschikbare dagen bij een
        // donker palet onzichtbaar werden. Bij kalenderstijl "donker" is de
        // kalender zelf al Primaire Kleur, dus daar omgekeerd.
        const highlightColor = isLight ? primaryColor : secondaryColor;
        const highlightTextColor = isLight ? buttonTextColor : primaryColor;

        return `
          .react-datepicker {
            font-family: ${brandingSettings.font}, sans-serif;
            background-color: ${bgColor};
            color: ${textColor};
            border: 1px solid ${textColor};
          }
          .react-datepicker__header {
            background-color: ${bgColor};
            border-bottom: 1px solid ${textColor};
          }
          .react-datepicker__current-month,
          .react-datepicker__day-name,
          .react-datepicker__day {
            color: ${textColor};
          }
          .react-datepicker__day--disabled,
          .react-datepicker__day--disabled:hover {
            background-color: transparent;
            color: ${textColor};
            opacity: 0.35;
          }
          .react-datepicker__day.available-date {
            background-color: ${highlightColor};
            color: ${highlightTextColor};
            border-radius: 50%;
            font-weight: bold;
          }
          .react-datepicker__day.available-date:hover {
            background-color: ${highlightColor};
            color: ${highlightTextColor};
            opacity: 0.8;
          }
          /* react-datepicker markeert standaard "vandaag" als keyboard-selected
             met een eigen lichtblauw bolletje — los van beschikbaarheid, dus neutraal. */
          .react-datepicker__day--keyboard-selected:not(.available-date):not(.react-datepicker__day--selected) {
            background-color: transparent;
          }
          .react-datepicker__day--selected,
          .react-datepicker__day--selected:hover {
            background-color: ${highlightColor};
            color: ${highlightTextColor};
            border-radius: 50%;
            font-weight: bold;
            opacity: 1;
            box-shadow: 0 0 0 2px ${bgColor}, 0 0 0 4px ${highlightColor};
          }
        `;
    };

    // teamMemberId optioneel: bij ontbreken wordt de eigen agenda van de
    // aanbieder gebruikt (bestaand gedrag, ongewijzigd) — bij een dienst met
    // teamleden vraagt de klant eerst voor wie hij boekt (zie renderTeamMemberSelection),
    // en wordt deze functie opnieuw aangeroepen met dat specifieke teamlid-id.
    const fetchAvailabilities = async (fetchedUserId, teamMemberId) => {
        console.log("Fetching availabilities for user ID:", fetchedUserId);
        if (!fetchedUserId) {
            console.log("No userId available, skipping availabilities fetch");
            return;
        }
        try {
            // Losse beschikbaarheden + vast weekschema samen (zie weeklySchedule.js).
            const basePath = teamMemberId
                ? `users/${fetchedUserId}/teamMembers/${teamMemberId}`
                : `users/${fetchedUserId}`;
            const availabilityData = await fetchCombinedAvailabilities(basePath);
            console.log("Availabilities fetched:", availabilityData);
            setAvailabilities(availabilityData);
        } catch (error) {
            console.error("Error fetching availabilities:", error);
        }
    };

    const fetchBookedSlots = async (targetUserId, teamMemberId) => {
        // targetUserId optioneel: bij de initiële load (Promise.all in fetchInitialData)
        // is de userId-state nog niet bijgewerkt op het moment dat deze functie
        // wordt aangeroepen (React state-updates zijn niet synchroon), dus moet het
        // net opgehaalde ID expliciet worden doorgegeven in plaats van te vertrouwen
        // op de (nog verouderde) userId uit de closure.
        const idToUse = targetUserId || userId;
        if (!idToUse) return;
        try {
            // Via een Cloud Function i.p.v. rechtstreeks Firestore: 'bookings'
            // is niet meer publiek leesbaar (zie firestore.rules) — deze
            // functie geeft enkel datum/tijd + duur terug, nooit klantgegevens.
            // teamMemberId optioneel: elk teamlid heeft een eigen, onafhankelijke
            // agenda (zie getBookedSlotsForProvider) — bij ontbreken wordt de
            // eigen agenda van de aanbieder gebruikt (bestaand gedrag).
            const getBookedSlotsForProvider = httpsCallable(functions, 'getBookedSlotsForProvider');
            const result = await getBookedSlotsForProvider({ userId: idToUse, teamMemberId });
            const bookedSlotsData = (result.data.slots || []).map(slot => ({
                dateTime: { seconds: Math.floor(new Date(slot.dateTime).getTime() / 1000) },
                duration: slot.duration
            }));
            setBookedSlots(bookedSlotsData);
            // Een verse lijst van de eigen agenda hoort ook in de bewaarde kopie, anders komt bij terug
            // een verouderde lijst terug.
            if (!teamMemberId && ownCalendarRef.current) {
                ownCalendarRef.current = { ...ownCalendarRef.current, bookedSlots: bookedSlotsData };
            }
        } catch (error) {
            console.error("Error fetching booked slots:", error);
        }
    };

    const getPageStyle = () => {
        if (isPremiumOrTester) {
            return {
                fontFamily: brandingSettings.font,
                backgroundColor: brandingSettings.secondaryColor,
                color: brandingSettings.primaryColor
            };
        }
        return {};
    };

    const getButtonStyle = () => {
        if (isPremiumOrTester) {
            return {
                backgroundColor: brandingSettings.primaryColor,
                color: brandingSettings.buttonTextColor,
            };
        }
        return {};
    };

    // Dunne wrappers rond de gedeelde, pure implementatie in utils/bookingSlots.js
    // (die ook ManageBookingPage.js gebruikt) — houden hier dezelfde
    // aanroepstijl (geen expliciete context-param nodig) als voorheen.
    // Gememoïseerd: de DatePicker roept dit per kalendercel aan (dayClassName).
    const availableDates = useMemo(
        () => getAvailDates(selectedService, { availabilities, services, bookedSlots, minimumNoticeHours: minimumBookingNoticeHours }),
        [selectedService, availabilities, services, bookedSlots, minimumBookingNoticeHours]
    );
    const getAvailableDates = () => availableDates;

    const calculateAvailableTimes = (date, serviceId) =>
        calcAvailableTimes(date, serviceId, { availabilities, services, bookedSlots, minimumNoticeHours: minimumBookingNoticeHours });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!userId || !selectedService || !selectedDate || !selectedTime || !customerName || !customerEmail) {
            setNotification({ type: 'error', message: intl.formatMessage({ id: 'publicBooking.fillAllFields' }) });
            return;
        }

        const selectedServiceObjForIntake = services.find(s => s.id === selectedService);
        const intakeQuestions = selectedServiceObjForIntake?.intakeQuestions || [];
        const missingRequired = intakeQuestions.some(q => q.required && !intakeAnswers[q.id]);
        if (missingRequired) {
            setNotification({ type: 'error', message: intl.formatMessage({ id: 'publicBooking.fillAllFields' }) });
            return;
        }
        // Label bewust mee opslaan (niet enkel de vraag-id) — zo blijft een
        // boeking leesbaar ook als de aanbieder de vraag later aanpast/verwijdert.
        const intakeAnswersArray = intakeQuestions
            .filter(q => intakeAnswers[q.id])
            .map(q => ({ label: q.label, answer: intakeAnswers[q.id] }));

        // 'self' wordt niet als teamMemberId opgeslagen (dat is exact hetzelfde
        // als "geen teamlid" — de eigenaar z'n eigen, bestaande agenda), enkel
        // een echt teamlid-id. Naam wordt mee gekopieerd (zelfde principe als
        // bij intake-antwoorden hierboven) zodat een boeking leesbaar blijft
        // ook als het teamlid later verwijderd wordt.
        const resolvedTeamMemberId = (selectedTeamMember && selectedTeamMember !== 'self') ? selectedTeamMember : undefined;
        const resolvedTeamMemberName = resolvedTeamMemberId
            ? (teamMembers.find(m => m.id === resolvedTeamMemberId)?.name || '')
            : undefined;

        const [hours, minutes] = selectedTime.split(':');
        const bookingDateTime = new Date(selectedDate);
        bookingDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

        // Snelle client-side voorcontrole (geen round-trip nodig) — de server
        // hieronder blijft de uiteindelijke autoriteit en herhaalt dezelfde check.
        if (bookingDateTime.getTime() - Date.now() < minimumBookingNoticeHours * 60 * 60 * 1000) {
            setNotification({ type: 'error', message: intl.formatMessage({ id: 'publicBooking.minimumNoticeNotMet' }, { hours: minimumBookingNoticeHours }) });
            return;
        }

        const selectedServiceObj = services.find(s => s.id === selectedService);
        const serviceDuration = selectedServiceObj ? parseInt(selectedServiceObj.duration) : 30;

        // "Bevestiging vereist" is een Plus/Premium-feature — isWaitlistEnabled
        // bevat exact dezelfde plus/premium/tester-check, hier hergebruikt
        // i.p.v. een eigen, identieke state bij te houden.
        const needsConfirmation = !!selectedServiceObj.confirmationRequired && isWaitlistEnabled;

        try {
            // Betaalde dienst ZONDER "bevestiging vereist": stuur de klant eerst
            // naar Stripe om te betalen — de boeking zelf wordt pas aangemaakt na
            // bevestigde betaling (via handleStripeConnectWebhook), niet hier al.
            // Bij "bevestiging vereist" wordt hieronder i.p.v. dat wél meteen een
            // (nog onbetaalde) boeking aangemaakt, net als bij een gratis dienst —
            // de klant betaalt pas NA bevestiging door de aanbieder, via een link
            // die de server dan genereert (confirmBooking.js/BookingList.js).
            if (selectedServiceObj.price > 0 && !needsConfirmation) {
                const createBookingCheckoutSession = httpsCallable(functions, 'createBookingCheckoutSession');
                const result = await createBookingCheckoutSession({
                    userId,
                    serviceId: selectedService,
                    customerName,
                    customerEmail,
                    customerNote,
                    intakeAnswers: intakeAnswersArray,
                    teamMemberId: resolvedTeamMemberId,
                    teamMemberName: resolvedTeamMemberName,
                    dateTime: bookingDateTime.toISOString(),
                    duration: serviceDuration
                });
                if (result.data && result.data.url) {
                    window.location.href = result.data.url;
                } else {
                    setNotification({ type: 'error', message: intl.formatMessage({ id: 'publicBooking.bookingFailed' }, { error: 'checkout' }) });
                }
                return;
            }

            // Eén server-side call i.p.v. de vorige 4-5 losse rondes (limietcheck,
            // overlapcheck, evt. Zoom-meeting, de schrijfactie zelf) — zie
            // functions/src/functions/booking/createPublicBooking.js. Overlapcheck
            // + schrijfactie gebeuren daar in dezelfde Firestore-transactie, wat de
            // vorige "eerst checken, dan pas schrijven"-race op eenzelfde slot sluit.
            const createPublicBooking = httpsCallable(functions, 'createPublicBooking');
            const result = await createPublicBooking({
                userId,
                serviceId: selectedService,
                teamMemberId: resolvedTeamMemberId,
                dateTime: bookingDateTime.toISOString(),
                customerName,
                customerEmail,
                customerNote,
                intakeAnswers: intakeAnswersArray
            });

            // Optimistisch lokaal toevoegen i.p.v. de volledige bezette-sloten-lijst
            // opnieuw op te vragen — we weten al exact wat we net geboekt hebben.
            // De useEffect op bookedSlots herberekent availableTimes hierna vanzelf.
            setBookedSlots(prev => [...prev, {
                dateTime: { seconds: Math.floor(new Date(result.data.dateTime).getTime() / 1000) },
                duration: result.data.duration
            }]);

            // De maandelijkse boekingsteller wordt server-side opgehoogd door de
            // sendBookingConfirmation Cloud Function (met adminrechten) — een anonieme
            // bezoeker mag/kan het account van de aanbieder hier niet zelf bijwerken.
            setMonthlyBookingCount(prevCount => prevCount + 1);

            setBookingConfirmed(true);
            setZoomMeetingUrl(result.data.zoomMeetingUrl || '');

        } catch (error) {
            console.error("Booking error:", error);
            if (error.code === 'functions/already-exists') {
                setNotification({ type: 'error', message: intl.formatMessage({ id: 'publicBooking.slotTaken' }) });
                await fetchBookedSlots(userId, resolvedTeamMemberId);
            } else if (error.code === 'functions/resource-exhausted') {
                // "te veel aanvragen" (ratelimiter) is iets anders dan "boekingslimiet van de aanbieder bereikt"
                const tooMany = error.details && error.details.reason === 'rate-limit';
                setNotification({ type: 'error', message: intl.formatMessage({ id: tooMany ? 'publicBooking.tooManyRequests' : 'publicBooking.bookingLimitReached' }) });
            } else if (error.code === 'functions/failed-precondition') {
                setNotification({ type: 'error', message: intl.formatMessage({ id: 'publicBooking.minimumNoticeNotMet' }, { hours: minimumBookingNoticeHours }) });
            } else {
                setNotification({ type: 'error', message: intl.formatMessage({ id: 'publicBooking.bookingFailed' }, { error: error.message }) });
            }
        }
    };

    const handleJoinWaitlist = async () => {
        if (!waitlistName || !waitlistEmail || !selectedDate || !selectedService) return;
        setIsJoiningWaitlist(true);
        try {
            const joinWaitlist = httpsCallable(functions, 'joinWaitlist');
            await joinWaitlist({
                userId,
                serviceId: selectedService,
                date: selectedDate.toISOString(),
                customerName: waitlistName,
                customerEmail: waitlistEmail
            });
            setWaitlistJoined(true);
        } catch (error) {
            console.error('Error joining waitlist:', error);
            setNotification({ type: 'error', message: error.message || intl.formatMessage({ id: 'publicBooking.waitlistError' }) });
        } finally {
            setIsJoiningWaitlist(false);
        }
    };

    // Na het kiezen van een dienst: heeft die dienst teamleden gekoppeld
    // (ServiceList.js), dan vraagt de klant eerst voor wie hij boekt
    // (renderTeamMemberSelection) — anders (verreweg de meeste diensten,
    // ongewijzigd bestaand gedrag) rechtstreeks door naar datum/tijd, met de
    // eigen agenda van de aanbieder.
    const restoreOwnCalendar = () => {
        if (ownCalendarRef.current) {
            setAvailabilities(ownCalendarRef.current.availabilities);
            setBookedSlots(ownCalendarRef.current.bookedSlots);
        }
    };

    const proceedAfterServiceSelected = async (serviceId) => {
        setSelectedService(serviceId);
        setIntakeAnswers({});
        setSelectedTeamMember(null);
        if (!serviceId) return;

        const serviceObj = services.find(s => s.id === serviceId);
        if (serviceObj?.teamMemberIds?.length > 0) {
            setCurrentStep('selectTeamMember');
        } else {
            // Eigen agenda van de aanbieder (geen teamlid): getPublicBookingPageData
            // heeft availabilities/bookedSlots hierboven al meegegeven (zelfde
            // filter, zie publicBookingPageData.js) — geen nieuwe round-trip nodig.
            restoreOwnCalendar();
            setCurrentStep('selectDateTime');
        }
    };

    const handleTeamMemberSelected = async (teamMemberId) => {
        setSelectedTeamMember(teamMemberId);
        if (teamMemberId === 'self') {
            // Idem: dit IS de eigen agenda van de aanbieder, al aanwezig uit de
            // initiële load — geen nieuwe round-trip nodig.
            restoreOwnCalendar();
            setCurrentStep('selectDateTime');
            return;
        }
        // Teamlid-specifieke agenda: pas gekend na deze klik, dus hier wél nodig
        // (zie comment in publicBookingPageData.js) — de twee onafhankelijke
        // calls parallel i.p.v. na elkaar.
        await Promise.all([
            fetchAvailabilities(userId, teamMemberId),
            fetchBookedSlots(userId, teamMemberId)
        ]);
        setCurrentStep('selectDateTime');
    };

    const renderTeamMemberSelection = () => {
        const serviceObj = services.find(s => s.id === selectedService);
        const ids = serviceObj?.teamMemberIds || [];
        return (
            <div className="booking-form" style={getPageStyle()}>
                {renderBackButton()}
                {customTitle && <h2 className="page-title">{customTitle}</h2>}
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                    {intl.formatMessage({ id: 'publicBooking.selectTeamMember' })}:
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {ids.map(id => (
                        <button
                            key={id}
                            type="button"
                            onClick={() => handleTeamMemberSelected(id)}
                            style={getButtonStyle()}
                        >
                            {id === 'self'
                                ? (customTitle || intl.formatMessage({ id: 'publicBooking.providerSelf' }))
                                : (teamMembers.find(m => m.id === id)?.name || id)}
                        </button>
                    ))}
                </div>
            </div>
        );
    };

    useEffect(() => {
        if (services.length === 1) {
            proceedAfterServiceSelected(services[0].id);
        }
    }, [services]);

    const renderServiceSelection = () => {
        if (services.length === 1) {
            return null; // Skip service selection if there's only one service
        }

        return (
            <div className="booking-form" style={getPageStyle()}>
                {customTitle && <h2 className="page-title">{customTitle}</h2>}
                <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                    <label htmlFor="service-select" style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                        {intl.formatMessage({ id: 'publicBooking.selectService' })}:
                    </label>
                    <select
                        id="service-select"
                        value={selectedService}
                        onChange={(e) => proceedAfterServiceSelected(e.target.value)}
                        required
                        style={isPremiumOrTester ? { borderColor: brandingSettings.primaryColor } : {}}
                    >
                        <option value="">{intl.formatMessage({ id: 'publicBooking.chooseService' })}</option>
                        {services.map(service => (
                            <option key={service.id} value={service.id}>{service.name}</option>
                        ))}
                    </select>
                </div>
            </div>
        );
    };

    const renderBookingForm = () => (
        <div className="booking-form" style={getPageStyle()}>
            <style>{getCalendarCustomStyles()}</style>
            {customTitle && <h2 className="page-title">{customTitle}</h2>}
            {selectedService && (
                <>
                    <div className="selected-service">
                        <div className="service-name">
                            {services.find(s => s.id === selectedService)?.name}
                        </div>
                        <div className="service-duration">
                            {intl.formatMessage({ id: 'publicBooking.duration' })}:&nbsp;
                            {services.find(s => s.id === selectedService)?.duration || 'N/A'}&nbsp;
                            {intl.formatMessage({ id: 'publicBooking.minutes' })}
                        </div>
                    </div>
                    <DatePicker
                        selected={selectedDate}
                        onChange={(date) => setSelectedDate(date)}
                        inline
                        minDate={new Date()}
                        includeDates={getAvailableDates()}
                        dayClassName={date =>
                            getAvailableDates().some(availableDate =>
                                date.toDateString() === availableDate.toDateString()
                            ) ? "available-date" : undefined
                        }
                    />

                    {selectedDate && availableTimes.length > 0 && (
                        <div className="time-selection">
                            {availableTimes.map((time, index) => (
                                <button
                                    key={index}
                                    onClick={() => {
                                        setSelectedTime(time);
                                        setCurrentStep('contactForm');
                                    }}
                                    style={getButtonStyle()}
                                >
                                    {time}
                                </button>
                            ))}
                        </div>
                    )}
                    {selectedDate && availableTimes.length === 0 && (
                        <p>{intl.formatMessage({ id: 'publicBooking.noTimesAvailable' })}</p>
                    )}
                </>
            )}
        </div>
    );

    const renderContactForm = () => (
        <div className={`contact-form ${isPremiumOrTester ? 'premium-user' : ''}`} style={getPageStyle()}>
            {renderBackButton()}
            {customTitle && <h2 className="page-title">{customTitle}</h2>}
            <div className="booking-summary" style={{ textAlign: 'center' }}>
                <p className="service-name">{services.find(s => s.id === selectedService)?.name}</p>
                <p>{intl.formatMessage({ id: 'publicBooking.duration' })}: {services.find(s => s.id === selectedService)?.duration || 'N/A'} {intl.formatMessage({ id: 'publicBooking.minutes' })}</p>
                <p>{intl.formatMessage({ id: 'publicBooking.date' })}: {selectedDate.toLocaleDateString()}</p>
                <p>{intl.formatMessage({ id: 'publicBooking.time' })}: {selectedTime}</p>
            </div>
            <form onSubmit={handleSubmit}>
                <input
                    type="text"
                    placeholder={intl.formatMessage({ id: 'publicBooking.name' })}
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    required
                    className={isPremiumOrTester ? 'premium-input' : ''}
                />
                <div className="email-input-container">
                    <input
                        type="email"
                        placeholder={intl.formatMessage({ id: 'publicBooking.email' })}
                        value={customerEmail}
                        onChange={(e) => setCustomerEmail(e.target.value)}
                        required
                        className={isPremiumOrTester ? 'premium-input' : ''}
                    />
                </div>
                <textarea
                    placeholder={intl.formatMessage({ id: 'publicBooking.note' })}
                    value={customerNote}
                    onChange={(e) => setCustomerNote(e.target.value)}
                    className={isPremiumOrTester ? 'premium-input' : ''}
                />
                {(services.find(s => s.id === selectedService)?.intakeQuestions || []).map(question => (
                    <div key={question.id} className="intake-question-field">
                        <label>
                            {question.label}{question.required && ' *'}
                        </label>
                        {question.type === 'textarea' && (
                            <textarea
                                value={intakeAnswers[question.id] || ''}
                                onChange={(e) => setIntakeAnswers(prev => ({ ...prev, [question.id]: e.target.value }))}
                                required={question.required}
                                className={isPremiumOrTester ? 'premium-input' : ''}
                            />
                        )}
                        {question.type === 'yesno' && (
                            <select
                                value={intakeAnswers[question.id] || ''}
                                onChange={(e) => setIntakeAnswers(prev => ({ ...prev, [question.id]: e.target.value }))}
                                required={question.required}
                                className={isPremiumOrTester ? 'premium-input' : ''}
                            >
                                <option value="">{intl.formatMessage({ id: 'publicBooking.chooseOption' })}</option>
                                <option value={intl.formatMessage({ id: 'publicBooking.yes' })}>{intl.formatMessage({ id: 'publicBooking.yes' })}</option>
                                <option value={intl.formatMessage({ id: 'publicBooking.no' })}>{intl.formatMessage({ id: 'publicBooking.no' })}</option>
                            </select>
                        )}
                        {question.type === 'text' && (
                            <input
                                type="text"
                                value={intakeAnswers[question.id] || ''}
                                onChange={(e) => setIntakeAnswers(prev => ({ ...prev, [question.id]: e.target.value }))}
                                required={question.required}
                                className={isPremiumOrTester ? 'premium-input' : ''}
                            />
                        )}
                    </div>
                ))}
                <button type="submit" style={getButtonStyle()}>
                    {isPremiumOrTester ? brandingSettings.buttonText : intl.formatMessage({ id: 'publicBooking.scheduleAppointment' })}
                </button>
            </form>
            {showPoweredBy && (
                <div className="branding">
                    <a href="http://flexitimebooking.com" target="_blank" rel="noopener noreferrer">
                        {intl.formatMessage({ id: 'publicBooking.poweredBy' })}
                    </a>
                </div>
            )}
        </div>
    );

    const renderConfirmation = () => (
        <div className={`booking-confirmation ${isPremiumOrTester ? 'premium-user' : ''}`} style={getPageStyle()}>
            <h2 className="page-title">{intl.formatMessage({ id: 'publicBooking.appointmentScheduled' })}</h2>
            <div className="confirmation-details">
                <p className='service-name'>{services.find(s => s.id === selectedService)?.name}</p>
                <p>{customerName}</p>
                <p>{selectedDate.toLocaleDateString()}, {selectedTime}</p>
                {zoomMeetingUrl && (
                    <p>
                        {intl.formatMessage({ id: 'publicBooking.zoomMeetingLink' })}:
                        <a href={zoomMeetingUrl} target="_blank" rel="noopener noreferrer">
                            {intl.formatMessage({ id: 'publicBooking.joinZoomMeeting' })}
                        </a>
                    </p>
                )}
            </div>
            <p>
                {(!!services.find(s => s.id === selectedService)?.confirmationRequired && isWaitlistEnabled)
                    ? intl.formatMessage({ id: 'publicBooking.pendingConfirmationSent' })
                    : intl.formatMessage({ id: 'publicBooking.confirmationSent' })}
            </p>
            {showPoweredBy && (
                <div className="branding">
                    <a href="http://flexitimebooking.com" target="_blank" rel="noopener noreferrer">
                        {intl.formatMessage({ id: 'publicBooking.poweredBy' })}
                    </a>
                </div>
            )}
        </div>
    );

    const renderCustomFooter = () => {
        if (isPremiumOrTester && customFooter) {
            // Altijd gefilterd (zie utils/sanitizeHtml.js): de footer is vrije HTML van de
            // aanbieder op een pagina van onze eigen origin.
            const safeFooter = sanitizeFooterHtml(customFooter);
            if (!safeFooter) return null;
            return (
                <div className="custom-footer" dangerouslySetInnerHTML={{ __html: safeFooter }} />
            );
        }
        return null;
    };

    // Eén stap terug in de boeking. De dienstkeuze valt weg bij één dienst en de teamlidkeuze bij een
    // dienst zonder teamleden, dus de volgorde hangt van de gekozen dienst af. De diepte is de
    // plaats van de huidige stap in die volgorde.
    const selectedServiceObj = services.find(s => s.id === selectedService);
    const hasTeamStep = (selectedServiceObj?.teamMemberIds?.length || 0) > 0;
    const bookingSteps = [
        ...(services.length > 1 ? ['selectService'] : []),
        ...(hasTeamStep ? ['selectTeamMember'] : []),
        'selectDateTime',
        'contactForm'
    ];
    const canGoBack = !bookingConfirmed && !paymentStatus && !invalidLink && bookingSteps.indexOf(currentStep) > 0;

    const goBack = () => {
        if (!canGoBack) return;
        if (currentStep === 'contactForm') {
            // De ingevulde gegevens blijven staan, alleen de gekozen tijd wordt weer open.
            setSelectedTime('');
            setCurrentStep('selectDateTime');
        } else if (currentStep === 'selectDateTime') {
            setSelectedDate(null);
            setSelectedTime('');
            setAvailableTimes([]);
            if (hasTeamStep) {
                setSelectedTeamMember(null);
                setCurrentStep('selectTeamMember');
            } else {
                setSelectedService('');
                setCurrentStep('selectService');
            }
        } else if (currentStep === 'selectTeamMember') {
            setSelectedService('');
            setSelectedTeamMember(null);
            setCurrentStep('selectService');
        }
    };

    // De terugknop van het toestel doet hetzelfde als de knop Terug, behalve in een iframe op de
    // site van de aanbieder. Daar zouden de extra geschiedenisregels in de geschiedenis van die
    // site belanden, dus daar werkt alleen de knop op de pagina.
    const inIframe = window.self !== window.top;
    useTerugDiepte(!inIframe && canGoBack ? bookingSteps.indexOf(currentStep) : 0, goBack);

    const renderBackButton = () => canGoBack && (
        <button type="button" className="booking-back-button" onClick={goBack}>
            ‹ {intl.formatMessage({ id: 'publicBooking.back' })}
        </button>
    );

    if (invalidLink) {
        return (
            <div className="public-booking-page">
                <div className="manage-booking-card">
                    <h2 className="page-title">{intl.formatMessage({ id: 'publicBooking.invalidLinkTitle' })}</h2>
                    <p style={{ textAlign: 'center' }}>{intl.formatMessage({ id: 'publicBooking.invalidLinkInfo' })}</p>
                </div>
            </div>
        );
    }

    // isBrandingLoaded wordt enkel true zodra er een geldige gebruiker gevonden is
    // (zie fetchBrandingSettings hierboven) — bij een ongeldige link blijft die dus
    // voorgoed false, vandaar de invalidLink-check hierboven vóór deze wachtstand,
    // anders bleef de pagina hier voor altijd op "Laden..." hangen.
    if (isLoading || !isBrandingLoaded) {
        // Hergebruik van de statische spinner-stijl uit public/index.html
        // (.app-boot-shell) i.p.v. kale, ongestylde tekst linksboven.
        return (
            <div className="app-boot-shell">
                <div className="app-boot-shell__spinner"></div>
            </div>
        );
    }

    return (
        <div className="public-booking-page" style={getPageStyle()} ref={pageRef}>
            {notification && (
                <div className={`notification notification-${notification.type}`}>
                    {notification.message}
                </div>
            )}
            {logoUrl && (
                <div className="company-logo">
                    <img src={logoUrl} alt="Company Logo" />
                </div>
            )}

            {isLoading ? (
                <div>{intl.formatMessage({ id: 'loading' })}</div>
            ) : paymentStatus === 'success' ? (
                <div className={`booking-confirmation ${isPremiumOrTester ? 'premium-user' : ''}`} style={getPageStyle()}>
                    <h2 className="page-title">{intl.formatMessage({ id: 'publicBooking.paymentSuccessTitle' })}</h2>
                    <p>{intl.formatMessage({ id: 'publicBooking.paymentSuccessInfo' })}</p>
                </div>
            ) : paymentStatus === 'cancelled' ? (
                <div className={`booking-confirmation ${isPremiumOrTester ? 'premium-user' : ''}`} style={getPageStyle()}>
                    <h2 className="page-title">{intl.formatMessage({ id: 'publicBooking.paymentCancelledTitle' })}</h2>
                    <p>{intl.formatMessage({ id: 'publicBooking.paymentCancelledInfo' })}</p>
                    <button onClick={() => setPaymentStatus(null)}>
                        {intl.formatMessage({ id: 'publicBooking.tryAgain' })}
                    </button>
                </div>
            ) : !bookingConfirmed ? (
                currentStep === 'selectService' ? renderServiceSelection() :
                    currentStep === 'selectTeamMember' ? renderTeamMemberSelection() :
                    currentStep === 'selectDateTime' ? (
                        <div className="booking-form" style={getPageStyle()}>
                            <style>{getCalendarCustomStyles()}</style>
                            {renderBackButton()}
                            {customTitle && <h2 className="page-title">{customTitle}</h2>}
                            {selectedService && (
                                <>
                                    <div className="selected-service">
                                        <div className="service-name">
                                            {services.find(s => s.id === selectedService)?.name}
                                        </div>
                                        <div className="service-duration">
                                            {intl.formatMessage({ id: 'publicBooking.duration' })}:&nbsp;
                                            {services.find(s => s.id === selectedService)?.duration || 'N/A'}&nbsp;
                                            {intl.formatMessage({ id: 'publicBooking.minutes' })}
                                        </div>
                                    </div>
                                    <DatePicker
                                        selected={selectedDate}
                                        onChange={(date) => setSelectedDate(date)}
                                        inline
                                        minDate={new Date()}
                                        includeDates={getAvailableDates()}
                                        dayClassName={date =>
                                            getAvailableDates().some(availableDate =>
                                                date.toDateString() === availableDate.toDateString()
                                            ) ? "available-date" : undefined
                                        }
                                    />
                                    {selectedDate && availableTimes.length > 0 && (
                                        <div className="time-selection">
                                            {availableTimes.map((time, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => {
                                                        setSelectedTime(time);
                                                        setCurrentStep('contactForm');
                                                    }}
                                                    style={getButtonStyle()}
                                                >
                                                    {time}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    {selectedDate && availableTimes.length === 0 && (
                                        <div>
                                            <p>{intl.formatMessage({ id: 'publicBooking.noTimesAvailable' })}</p>
                                            {isWaitlistEnabled && (
                                                waitlistJoined ? (
                                                    <p className="waitlist-joined">{intl.formatMessage({ id: 'publicBooking.waitlistJoined' })}</p>
                                                ) : (
                                                    <div className="waitlist-form">
                                                        <p className="waitlist-info">{intl.formatMessage({ id: 'publicBooking.waitlistInfo' })}</p>
                                                        <input
                                                            type="text"
                                                            placeholder={intl.formatMessage({ id: 'publicBooking.name' })}
                                                            value={waitlistName}
                                                            onChange={(e) => setWaitlistName(e.target.value)}
                                                        />
                                                        <input
                                                            type="email"
                                                            placeholder={intl.formatMessage({ id: 'publicBooking.email' })}
                                                            value={waitlistEmail}
                                                            onChange={(e) => setWaitlistEmail(e.target.value)}
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={handleJoinWaitlist}
                                                            disabled={isJoiningWaitlist || !waitlistName || !waitlistEmail}
                                                            style={getButtonStyle()}
                                                        >
                                                            {isJoiningWaitlist
                                                                ? intl.formatMessage({ id: 'publicBooking.waitlistJoining' })
                                                                : intl.formatMessage({ id: 'publicBooking.waitlistJoinButton' })}
                                                        </button>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    ) :
                        renderContactForm()
            ) : (
                renderConfirmation()
            )}

            {renderCustomFooter()}
        </div>
    );
}

export default PublicBookingPage;